import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import CustomDatePicker from '../../components/CustomDatePicker';
import { getSubDCAByDCA, getInvoiceGSTConfig } from '../../api/SupplierPOAPI/supplierPOInvoiceAPI';
import { S3_BASE_URL, UPLOAD_DOCS_PATH, S3_FOLDERS } from '../../config/s3Config';

import {
    fetchMRRForUser, fetchVendorDetailsByMRR, fetchCompanyGSTNos,
    fetchItemsByMRRNo, fetchAnlyzeTax,
    fetchTransportDCAs, fetchOtherChargeDCAs, fetchDeductionDCAs,
    checkBudget, submitSupplierPOInvoice,
    clearVendorDetails, clearItemsData, clearTaxAnalysis, clearSaveResult,
    resetSupplierPOInvoice,
    selectSupInvMRRList, selectSupInvVendorDetails, selectSupInvCompanyGSTNos,
    selectSupInvItemsData, selectSupInvTaxAnalysis,
    selectSupInvTransportDCAs, selectSupInvOtherDCAs, selectSupInvDeductionDCAs,
    selectSupInvBudgetResult, selectSupInvSaveResult,
    selectSupInvMRRListLoading, selectSupInvVendorDetailsLoading,
    selectSupInvCompanyGSTLoading, selectSupInvItemsDataLoading,
    selectSupInvTaxAnalysisLoading, selectSupInvTransportDCAsLoading,
    selectSupInvOtherDCAsLoading, selectSupInvDeductionDCAsLoading,
    selectSupInvBudgetLoading, selectSupInvSaveLoading, selectSupInvSaveError,
} from '../../slices/supplierPOSlice/supplierPOInvoiceSlice';

import {
    Receipt, Plus, Trash2, RotateCcw, Send,
    Loader2, ChevronDown, IndianRupee, ShieldCheck,
    FileText, AlertCircle, CheckCircle2, Layers, Calculator, MapPin,
    Truck, Package, X, Paperclip, Upload, CloudUpload,
    RefreshCw, ArrowLeftRight,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2,'0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const fmt = (v) => {
    const n = parseFloat(v);
    if ((!v && v !== 0) || isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ── UI Primitives (compact — matches ClientInvoiceCreation) ───────────────────
const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const readonlyCls = `${inputCls} bg-gray-50 dark:bg-gray-700 cursor-default`;

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

const Card = ({ children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {children}
    </div>
);

// Sub-section heading inside a card, with optional right-side content
const SubHeading = ({ icon: Icon, children, right }) => (
    <div className="flex items-center justify-between gap-3 mb-2">
        <p className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            <Icon className="h-3.5 w-3.5 text-indigo-500" /> {children}
        </p>
        {right}
    </div>
);

// Small read-only info cell (PO auto-filled details)
const InfoBadge = ({ label, value }) => (
    <div className="px-2.5 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/30 min-w-0">
        <p className="text-[10px] text-indigo-500 dark:text-indigo-400 font-semibold uppercase tracking-wide">{label}</p>
        <p className="text-xs font-bold text-indigo-800 dark:text-indigo-200 truncate" title={value || ''}>{value || '—'}</p>
    </div>
);

// Summary panel row
const SumRow = ({ label, value, className = '' }) => (
    <div className={`flex justify-between gap-3 ${className}`}>
        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
    </div>
);

const YesNoToggle = ({ value, onChange, disabled }) => (
    <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
        {['Yes', 'No'].map(opt => (
            <button key={opt} type="button" onClick={() => !disabled && onChange(opt)} disabled={disabled}
                className={`px-4 py-1 text-xs font-semibold transition-all disabled:opacity-50 ${
                    value === opt
                        ? opt === 'Yes' ? 'bg-green-500 text-white' : 'bg-rose-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
            >{opt}</button>
        ))}
    </div>
);

// ── Initial State ─────────────────────────────────────────────────────────────
const INITIAL_FORM = {
    MRRNo: '',
    // Auto-filled from vendor details
    VendorId: '', VendorName: '', PONo: '', CCCode: '', DCACode: '',
    Advance: 0, VendorType: '', isAssetPO: false,
    // Invoice details
    InvoiceNo: '', InvoiceDate: '', InvoiceMakingDate: '',
    // GST — empty string means "not yet chosen"; prevents auto-fetch before user decides
    TaxApplicable: '',
    // Legacy GSTType radio: 'Creditable' | 'Non-Creditable' (drives the company GST list and the Input GST config)
    GSTType: '',
    CompanyGST: '', VendorGST: '',
    // TCS
    TCSApplicable: 'No', TCSAmount: '',
    // Transport
    TransportDCA: '', TransportSubDCA: '',
    TransportAmount: '', TransportGSTPct: '',
    // Exchange
    isExchangeExist: 'No', ExchangeItem: '', ExItemAmount: '',
    // E-Way Bill
    EWaybillApplicable: 'No',
    EWaybillNumber: '', VehicleNumber: '', TransporterName: '', LRNumber: '', EWaybillDate: '',
    // Financial adjustments
    Retention: '', Hold: '',
};

let _rowId = 0;
const newRow = () => ({ id: ++_rowId, dcaCode: '', sdcaCode: '', amount: '' });

// ── Component ─────────────────────────────────────────────────────────────────
const SupplierPOInvoiceCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector(s => s.auth);
    const roleId    = userData?.roleId    || userData?.RID || 0;
    const userId    = userData?.userId    || userData?.UID || userData?.employeeId || '';
    const userName  = userData?.userName  || userData?.username || 'User';
    const createdBy = userName;

    // Redux selectors
    const mrrList           = useSelector(selectSupInvMRRList);
    const vendorDetails     = useSelector(selectSupInvVendorDetails);
    const companyGSTNos     = useSelector(selectSupInvCompanyGSTNos);
    const itemsData         = useSelector(selectSupInvItemsData);
    const taxAnalysis       = useSelector(selectSupInvTaxAnalysis);
    const transportDCAs     = useSelector(selectSupInvTransportDCAs);
    const otherDCAs         = useSelector(selectSupInvOtherDCAs);
    const deductionDCAs     = useSelector(selectSupInvDeductionDCAs);
    const budgetResult      = useSelector(selectSupInvBudgetResult);
    const saveResult        = useSelector(selectSupInvSaveResult);
    const saveError         = useSelector(selectSupInvSaveError);

    const loadingMRRList        = useSelector(selectSupInvMRRListLoading);
    const loadingVendorDetails  = useSelector(selectSupInvVendorDetailsLoading);
    const loadingCompanyGST     = useSelector(selectSupInvCompanyGSTLoading);
    const loadingItemsData      = useSelector(selectSupInvItemsDataLoading);
    const loadingTaxAnalysis    = useSelector(selectSupInvTaxAnalysisLoading);
    const loadingTransportDCAs  = useSelector(selectSupInvTransportDCAsLoading);
    const loadingOtherDCAs      = useSelector(selectSupInvOtherDCAsLoading);
    const loadingDeductDCAs     = useSelector(selectSupInvDeductionDCAsLoading);
    const loadingBudget         = useSelector(selectSupInvBudgetLoading);
    const loadingSave           = useSelector(selectSupInvSaveLoading);

    // Local state
    const [form, setForm]                 = useState(INITIAL_FORM);
    const [otherCharges, setOtherCharges] = useState([newRow()]);
    const [deductions,   setDeductions]   = useState([newRow()]);
    const [subDCACache,    setSubDCACache]    = useState({});
    const [loadingSubDCAs, setLoadingSubDCAs] = useState({});
    const [budgetChecked,  setBudgetChecked]  = useState(false);

    // File upload state
    const [attachedFile,   setAttachedFile]   = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus,   setUploadStatus]   = useState('idle'); // idle | ready | uploading | error
    const [uploadError,    setUploadError]    = useState('');
    const fileInputRef = useRef(null);

    const isBusy = loadingSave || loadingBudget || uploadStatus === 'uploading';

    // Derived vendor tax list (GST numbers)
    const vendorTaxList = vendorDetails?.vendorTaxlist || [];

    // ── SubDCA fetch (local cache) ─────────────────────────────────────────────
    const fetchSubDCAs = async (dcaCode) => {
        if (!dcaCode || subDCACache[dcaCode] !== undefined) return;
        setLoadingSubDCAs(p => ({ ...p, [dcaCode]: true }));
        try {
            const res = await getSubDCAByDCA(dcaCode);
            setSubDCACache(p => ({ ...p, [dcaCode]: res.data?.Data || [] }));
        } catch {
            setSubDCACache(p => ({ ...p, [dcaCode]: [] }));
        } finally {
            setLoadingSubDCAs(p => ({ ...p, [dcaCode]: false }));
        }
    };

    // ── Bootstrap ─────────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchMRRForUser({ roleId, userId }));
        dispatch(fetchCompanyGSTNos());
        dispatch(fetchTransportDCAs());
        return () => dispatch(resetSupplierPOInvoice());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // ── Vendor details → form ─────────────────────────────────────────────────
    useEffect(() => {
        if (!vendorDetails) return;
        setForm(f => ({
            ...f,
            VendorId:   vendorDetails.VendorCode || '',
            VendorName: vendorDetails.VendorName  || '',
            PONo:       vendorDetails.PONo        || '',
            CCCode:     vendorDetails.CCCode      || '',
            DCACode:    vendorDetails.DcaCode     || '',
            Advance:    vendorDetails.Advance     || 0,
            VendorType: vendorDetails.VendorType  || '',
            isAssetPO:  vendorDetails.isAssetPO   || false,
        }));
    }, [vendorDetails]);

    // ── Same-state detection from GST number prefixes ─────────────────────────
    // First 2 characters of a GST number are the state code (e.g. "22" = Gujarat)
    const sameState = form.TaxApplicable === 'Yes' && form.VendorGST && form.CompanyGST
        ? form.VendorGST.substring(0, 2) === form.CompanyGST.substring(0, 2)
        : false;

    // ── MRRNo change → fetch other/deduction DCAs ─────────────────────────────
    useEffect(() => {
        if (!form.MRRNo) return;
        dispatch(fetchOtherChargeDCAs(form.MRRNo));
        dispatch(fetchDeductionDCAs(form.MRRNo));
    }, [form.MRRNo, dispatch]);

    // ── Fetch items — only after TaxApplicable is explicitly chosen ───────────
    // 'Yes' path: waits for both GST numbers (backend needs them to compute CGST/SGST vs IGST)
    // 'No'  path: fetches immediately once MRR is set (no GST numbers needed)
    // ''    path: user hasn't decided yet — do nothing
    useEffect(() => {
        if (!form.MRRNo || !form.TaxApplicable) return;
        if (form.TaxApplicable === 'Yes' && form.CompanyGST && form.VendorGST) {
            dispatch(fetchItemsByMRRNo({
                gstNo: form.CompanyGST, vendorGstNo: form.VendorGST,
                mrrNo: form.MRRNo, vendorType: form.VendorType,
            }));
            dispatch(fetchAnlyzeTax({
                gstNo: form.CompanyGST, vendorGstNo: form.VendorGST,
                mrrNo: form.MRRNo,
            }));
        } else if (form.TaxApplicable === 'No') {
            dispatch(fetchItemsByMRRNo({
                gstNo: '', vendorGstNo: '', mrrNo: form.MRRNo, vendorType: form.VendorType,
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.TaxApplicable, form.CompanyGST, form.VendorGST, form.MRRNo, dispatch]);

    // ── Save errors ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (saveError) toast.error(String(saveError));
    }, [saveError]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

    const handleMRRChange = (mrrNo) => {
        dispatch(clearVendorDetails());
        dispatch(clearItemsData());
        dispatch(clearTaxAnalysis());
        setBudgetChecked(false);
        setForm({ ...INITIAL_FORM, MRRNo: mrrNo });
        if (mrrNo) dispatch(fetchVendorDetailsByMRR(mrrNo));
    };

    const handleTaxApplicableChange = (val) => {
        setForm(f => ({ ...f, TaxApplicable: val, GSTType: '', CompanyGST: '', VendorGST: '' }));
    };

    // Legacy GSTType change: reload the company GST list for that type and clear the selection.
    const handleGSTTypeChange = (val) => {
        setForm(f => ({ ...f, GSTType: val, CompanyGST: '' }));
        if (val) dispatch(fetchCompanyGSTNos(val));
    };

    // Other charges rows
    const addOtherRow    = ()         => setOtherCharges(r => [...r, newRow()]);
    const removeOtherRow = (id)       => setOtherCharges(r => r.filter(x => x.id !== id));
    const updateOtherRow = (id, k, v) => {
        setOtherCharges(r => r.map(x => x.id === id ? { ...x, [k]: v } : x));
        if (k === 'dcaCode' && v) fetchSubDCAs(v);
    };

    // Deduction rows
    const addDeductRow    = ()         => setDeductions(r => [...r, newRow()]);
    const removeDeductRow = (id)       => setDeductions(r => r.filter(x => x.id !== id));
    const updateDeductRow = (id, k, v) => {
        setDeductions(r => r.map(x => x.id === id ? { ...x, [k]: v } : x));
        if (k === 'dcaCode' && v) fetchSubDCAs(v);
    };

    // ── File upload ───────────────────────────────────────────────────────────
    const handleFileSelect = (file) => {
        if (!file) return;
        if (file.type !== 'application/pdf') { toast.error('Only PDF files are allowed.'); return; }
        if (file.size > 10 * 1024 * 1024)    { toast.error('File size must be under 10 MB.'); return; }
        setAttachedFile(file);
        setUploadStatus('ready');
        setUploadProgress(0);
        setUploadError('');
    };

    const handleRemoveFile = () => {
        setAttachedFile(null);
        setUploadProgress(0);
        setUploadStatus('idle');
        setUploadError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── Calculations ──────────────────────────────────────────────────────────
    const mrrItems  = itemsData?.MRRItemData || [];
    const itemTotal = itemsData?.ItemTotal != null
        ? parseFloat(itemsData.ItemTotal)
        : mrrItems.reduce((s, i) => s + (parseFloat(i.Amount) || 0), 0);
    // taxAnalysis (GetAnlyzeTaxbyMRRNo) gives state-adjusted CGST/SGST vs IGST;
    // fall back to itemsData totals, then item-level reduce
    const gstSource = taxAnalysis || itemsData;
    const cgstTotal = form.TaxApplicable === 'Yes'
        ? (gstSource?.CGSTTotal != null
            ? parseFloat(gstSource.CGSTTotal)
            : mrrItems.reduce((s, i) => s + (parseFloat(i.CGSTAmt) || 0), 0))
        : 0;
    const sgstTotal = form.TaxApplicable === 'Yes'
        ? (gstSource?.SGSTTotal != null
            ? parseFloat(gstSource.SGSTTotal)
            : mrrItems.reduce((s, i) => s + (parseFloat(i.SGSTAmt) || 0), 0))
        : 0;
    const igstTotal = form.TaxApplicable === 'Yes'
        ? (gstSource?.IGSTTotal != null
            ? parseFloat(gstSource.IGSTTotal)
            : mrrItems.reduce((s, i) => s + (parseFloat(i.IGSTAmt) || 0), 0))
        : 0;
    const gstTotal    = cgstTotal + sgstTotal + igstTotal;
    const transportAmt      = parseFloat(form.TransportAmount) || 0;
    const transportGSTPct   = parseFloat(form.TransportGSTPct) || 0;
    const transportGSTAmt   = transportAmt > 0 ? +(transportAmt * transportGSTPct / 100).toFixed(2) : 0;
    const transportCGSTCalc = sameState ? +(transportGSTAmt / 2).toFixed(2) : 0;
    const transportSGSTCalc = sameState ? +(transportGSTAmt / 2).toFixed(2) : 0;
    const transportIGSTCalc = !sameState ? transportGSTAmt : 0;
    const transportGST      = transportGSTAmt;
    const otherTotal  = otherCharges.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const deductTotal = deductions.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const tcsAmount   = form.TCSApplicable === 'Yes' ? (parseFloat(form.TCSAmount) || 0) : 0;
    const exchangeAmt = form.isExchangeExist === 'Yes' ? (parseFloat(form.ExItemAmount) || 0) : 0;

    const invoiceValue = itemTotal + gstTotal + transportAmt + transportGST + otherTotal;
    const netAmount    = invoiceValue - deductTotal
        - (parseFloat(form.Retention) || 0)
        - (parseFloat(form.Hold) || 0)
        - (parseFloat(form.Advance) || 0)
        + tcsAmount - exchangeAmt;

    // ── Build charge strings ──────────────────────────────────────────────────
    const buildChargeStrings = (rows) => {
        const valid = rows.filter(r => r.dcaCode && r.sdcaCode && r.amount);
        return {
            // Trailing comma per element like legacy: spInsertSupplierPOInvoice loops
            // WHILE CHARINDEX(',', @list) <> 0, so an unterminated last element is skipped.
            dcas:    valid.length ? valid.map(r => `${r.dcaCode},`).join('')  : null,
            sdcas:   valid.length ? valid.map(r => `${r.sdcaCode},`).join('') : null,
            amounts: valid.length ? valid.map(r => `${r.amount},`).join('')   : null,
        };
    };

    const buildItemStrings = () => {
        const items = itemsData?.MRRItemData || [];
        if (!items.length) return { ids: null, amts: null, cgst: null, sgst: null, igst: null };
        // Use taxAnalysis MRRItemData for state-adjusted GST percents (CGST/SGST vs IGST)
        // Fall back to itemsData if taxAnalysis not yet loaded
        const taxItems = taxAnalysis?.MRRItemData || items;
        return {
            ids:  items.map(i => `${i.Id},`).join(''),
            amts: items.map(i => `${i.Amount || 0},`).join(''),
            cgst: taxItems.map(i => `${i.CGSTpercent || 0},`).join(''),
            sgst: taxItems.map(i => `${i.SGSTpercent || 0},`).join(''),
            igst: taxItems.map(i => `${i.IGSTpercent || 0},`).join(''),
        };
    };

    // ── Validate ──────────────────────────────────────────────────────────────
    const validateForm = () => {
        if (!form.MRRNo)
            { toast.error('Select an MRR number.'); return false; }
        if (!form.TaxApplicable)
            { toast.error('Please select whether GST is applicable.'); return false; }
        if (!form.InvoiceNo.trim())
            { toast.error('Invoice number is required.'); return false; }
        if (!form.InvoiceDate)
            { toast.error('Invoice date is required.'); return false; }
        if (!form.InvoiceMakingDate)
            { toast.error('Invoice making date is required.'); return false; }
        if (!itemsData || itemTotal <= 0)
            { toast.error('Item data not loaded or basic value is zero.'); return false; }
        if (form.TaxApplicable === 'Yes' && !form.GSTType)
            { toast.error('Select GST Type (Creditable / Non-Creditable).'); return false; }
        if (form.TaxApplicable === 'Yes' && (!form.VendorGST || !form.CompanyGST))
            { toast.error('Select vendor and company GST numbers.'); return false; }
        if (form.TCSApplicable === 'Yes' && !(parseFloat(form.TCSAmount) > 0))
            { toast.error('Enter TCS amount.'); return false; }
        if (form.EWaybillApplicable === 'Yes') {
            if (!form.EWaybillNumber.trim() || !form.VehicleNumber.trim())
                { toast.error('E-Way bill number and vehicle number are required.'); return false; }
        }
        const invalidOther = otherCharges.some(r => (r.dcaCode || r.amount) && !r.sdcaCode);
        if (invalidOther)
            { toast.error('Sub-DCA is mandatory for all Other Charges rows.'); return false; }
        const invalidDeduct = deductions.some(r => (r.dcaCode || r.amount) && !r.sdcaCode);
        if (invalidDeduct)
            { toast.error('Sub-DCA is mandatory for all Deduction rows.'); return false; }
        return true;
    };

    // ── Budget Check ──────────────────────────────────────────────────────────
    // Same fields legacy CheckSuppleirInvoiceBudget posts (spCheckBudgetForSupplirPOInvoice): item ids,
    // basic value, PO, CC, transport, other charges, GST type and GST total (Non-Creditable GST is a cost).
    const buildBudgetPayload = () => {
        const other = buildChargeStrings(otherCharges);
        const items = buildItemStrings();
        return {
            Itemids:            items.ids,
            BasicValue:         itemTotal,
            PONo:               form.PONo,
            CCCode:             form.CCCode,
            TransportAmount:    transportAmt,
            Otherchargedcas:    other.dcas,
            Otherchargeamounts: other.amounts,
            GSTType:            form.TaxApplicable === 'Yes' ? form.GSTType : 'Creditable',
            InvoiceDate:        formatDateForAPI(form.InvoiceDate),
            InvGSTTotal:        +(gstTotal + transportGST).toFixed(2),
        };
    };

    // spCheckBudgetForSupplirPOInvoice returns the literal 'sufficent' (sic) when the budget is enough.
    const isBudgetOk = (msg) => /^suffic?i?ent$/i.test(String(msg || '').trim());

    const handleCheckBudget = () => {
        if (!form.CCCode || !form.InvoiceDate || !itemTotal) {
            toast.warning('Complete MRR selection and invoice date before checking budget.');
            return;
        }
        dispatch(checkBudget(buildBudgetPayload())).then(res => {
            const msg = res?.payload;
            if (isBudgetOk(msg)) {
                toast.success('Budget check passed!');
                setBudgetChecked(true);
            } else {
                toast.error(String(msg || 'Budget check failed'));
                setBudgetChecked(false);
            }
        });
    };

    // ── Direct S3 PUT ─────────────────────────────────────────────────────────
    const uploadToS3 = (file, s3Key, onProgress) =>
        new Promise((resolve, reject) => {
            const url = `${S3_BASE_URL}/${UPLOAD_DOCS_PATH}/${S3_FOLDERS.VENDOR_INVOICES}/${s3Key}`;
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable && onProgress)
                    onProgress(Math.round((e.loaded / e.total) * 100));
            });
            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) resolve();
                else reject(new Error(`S3 upload failed: HTTP ${xhr.status}`));
            });
            xhr.addEventListener('error', () => reject(new Error('Network error during S3 upload')));
            xhr.open('PUT', url);
            xhr.setRequestHeader('Content-Type', 'application/pdf');
            xhr.send(file);
        });

    // ── Submit: Phase 1 save → Phase 2 S3 PUT ────────────────────────────────
    const handleSubmit = async () => {
        if (!validateForm()) return;

        // Legacy checkInputGSTConfig(): with GST, the Input GST config must be complete (4 heads) before saving.
        let gstConfig = null;
        if (form.TaxApplicable === 'Yes') {
            try {
                const res = await getInvoiceGSTConfig({
                    CompanyGST: form.CompanyGST, VendorClientGSTNo: form.VendorGST, InvoiceType: 'Vendor',
                    Cccode: form.CCCode, Taxtype: form.GSTType, PONO: form.PONo,
                    InvDate: formatDateForAPI(form.InvoiceDate), Configtyp: 'Input GST', VendorOrClient: form.VendorType,
                });
                gstConfig = res.data?.Data || res.data;
            } catch (err) {
                toast.error(err?.response?.data?.Message || 'Failed to read Input GST configuration');
                return;
            }
            if (Number(gstConfig?.ConfigCount) !== 4) {
                toast.error('Input GST Data Is Not Configured');
                return;
            }
        }

        const buildPayload = () => {
            const other  = buildChargeStrings(otherCharges);
            const deduct = buildChargeStrings(deductions);
            const items  = buildItemStrings();

            const payload = {
                // Field names must match the C# model (InvData) properties exactly
                VendorId:            form.VendorId,
                VendorName:          form.VendorName,
                MRR:                 form.MRRNo,
                PONo:                form.PONo,
                InvoiceNo:           form.InvoiceNo,
                InvoiceDate:         formatDateForAPI(form.InvoiceDate),
                InvoiceMakingDate:   formatDateForAPI(form.InvoiceMakingDate),
                CCCode:              form.CCCode,
                AccountHead:         form.DCACode,       // SP @DCACode ← InvData.AccountHead
                BasicValue:          itemTotal,
                NetAmount:           netAmount,
                VendorType:          form.VendorType,
                // Legacy invoice GST totals include the transport GST (txtInvTotal*GSTAmt / InvGSTTotal).
                CGSTTotal:           +(cgstTotal + transportCGSTCalc).toFixed(2),   // SP @Cgst
                SGSTTotal:           +(sgstTotal + transportSGSTCalc).toFixed(2),   // SP @Sgst
                IGSTTotal:           +(igstTotal + transportIGSTCalc).toFixed(2),   // SP @Igst
                InvGSTTotal:         +(gstTotal + transportGST).toFixed(2),         // SP @GSTtaxamt
                Itemids:             items.ids,
                ItemAmts:            items.amts,         // SP @Basicamts ← InvData.ItemAmts
                CGSTpercents:        items.cgst,
                SGSTpercents:        items.sgst,
                IGSTpercents:        items.igst,
                Otherchargedcas:     other.dcas,
                Otherchargesdcas:    other.sdcas,
                Otherchargeamounts:  other.amounts,
                Deductiondcas:       deduct.dcas,
                Deductionsdcas:      deduct.sdcas,
                Deductionamounts:    deduct.amounts,
                TransportDCA:        form.TransportDCA || 0,
                TransportSubDCA:     form.TransportSubDCA || 0,
                TransportAmount:     transportAmt,
                TransportCGSTAmt:    transportCGSTCalc,
                TransportSGSTAmt:    transportSGSTCalc,
                TransportIGSTAmt:    transportIGSTCalc,
                Advance:             parseFloat(form.Advance) || 0,
                TaxApplicable:       form.TaxApplicable || 'No',
                TCSApplicable:       form.TCSApplicable,
                TCSAmount:           tcsAmount,
                ExchangeItem:        form.isExchangeExist === 'Yes' ? form.ExchangeItem : null,
                ExItemAmount:        exchangeAmt,
                isExchangeExist:     form.isExchangeExist,
                EWaybillApplicable:  form.EWaybillApplicable,
                CreatedBy:           String(createdBy),
                RoleId:              Number(roleId),
                Filechk:             attachedFile ? 'Yes' : 'No',
                Extension:           attachedFile ? 'pdf' : '',
            };

            if (form.TaxApplicable === 'Yes') {
                payload.CompanyGST  = form.CompanyGST;   // SP @GSTno ← InvData.CompanyGST
                payload.VendorGST   = form.VendorGST;    // SP @VendorGstno ← InvData.VendorGST
                payload.GSTType     = form.GSTType;      // SP @GSTtaxtype ← InvData.GSTType
                // Input GST account head / sub heads from GetInvoiceGSTConfigDCASDCA (legacy checkInputGSTConfig)
                payload.Taxdca      = gstConfig?.DCAcode    || '';
                payload.Cgstsdca    = gstConfig?.CGSTSubDCA || '';
                payload.Sgstsdca    = gstConfig?.SGSTSubDCA || '';
                payload.Igstsdca    = gstConfig?.IGSTSubDCA || '';
            }

            if (form.EWaybillApplicable === 'Yes') {
                payload.EWaybillNumber  = form.EWaybillNumber;
                payload.VehicleNumber   = form.VehicleNumber;
                payload.TransporterName = form.TransporterName;
                payload.LRNumber        = form.LRNumber;
                payload.EWaybillDate    = formatDateForAPI(form.EWaybillDate);
            }

            return payload;
        };

        const doSave = async () => {
            const result   = await dispatch(submitSupplierPOInvoice(buildPayload()));
            const apiResp  = result?.payload;                   // full { Data, IsSuccessful, Message }
            const response = typeof apiResp === 'string'
                ? apiResp
                : String(apiResp?.Data || '');

            // SP signals success by setting @AddInvoicestatus = "Submited"
            if (!response.toLowerCase().startsWith('submited') && !response.toLowerCase().startsWith('submitted')) {
                const errMsg = (typeof apiResp === 'object' ? apiResp?.Message : null)
                    || response || 'Failed to save Supplier PO Invoice.';
                toast.error(errMsg);
                dispatch(clearSaveResult());
                return;
            }

            if (attachedFile) {
                const s3Key = response.includes(',') ? response.split(',')[1]?.trim() : null;
                if (s3Key) {
                    setUploadStatus('uploading');
                    setUploadProgress(0);
                    try {
                        await uploadToS3(attachedFile, s3Key, (pct) => setUploadProgress(pct));
                        toast.success('Invoice submitted and PDF uploaded successfully!');
                    } catch (err) {
                        toast.warning(`Invoice saved (${s3Key}) but PDF upload failed: ${err.message}. You can re-upload later.`);
                        setUploadStatus('error');
                        setUploadError(err.message);
                        dispatch(clearSaveResult());
                        return;
                    }
                } else {
                    toast.success('Supplier PO Invoice submitted successfully!');
                }
            } else {
                toast.success('Supplier PO Invoice submitted successfully!');
            }

            dispatch(clearSaveResult());
            handleReset();
        };

        // Legacy runs the budget check on every submit and stops unless it is sufficient.
        const budgetRes = await dispatch(checkBudget(buildBudgetPayload()));
        const budgetMsg = budgetRes?.payload;
        if (!isBudgetOk(budgetMsg)) {
            toast.error(String(budgetMsg || 'Budget check failed'));
            setBudgetChecked(false);
            return;
        }
        setBudgetChecked(true);
        await doSave();
    };

    const handleReset = () => {
        setForm(INITIAL_FORM);
        setOtherCharges([newRow()]);
        setDeductions([newRow()]);
        setSubDCACache({});
        setLoadingSubDCAs({});
        setBudgetChecked(false);
        setAttachedFile(null);
        setUploadProgress(0);
        setUploadStatus('idle');
        setUploadError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        dispatch(resetSupplierPOInvoice());
        dispatch(fetchMRRForUser({ roleId, userId }));
        dispatch(fetchCompanyGSTNos());
        dispatch(fetchTransportDCAs());
    };

    // ── Row renderer ──────────────────────────────────────────────────────────
    const renderChargeRows = (rows, dcaList, loadingDCA, updateFn, removeFn) => (
        <div className="space-y-2">
            {rows.map((row, idx) => {
                const rowSubDCAs    = row.dcaCode ? (subDCACache[row.dcaCode] || []) : [];
                const isLoadingSDCA = row.dcaCode && loadingSubDCAs[row.dcaCode];
                return (
                    <div key={row.id} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-12 sm:col-span-5">
                            {idx === 0 && <Label required>DCA</Label>}
                            <div className="relative">
                                <select className={`${inputCls} appearance-none pr-8`}
                                    value={row.dcaCode}
                                    onChange={e => { updateFn(row.id, 'dcaCode', e.target.value); updateFn(row.id, 'sdcaCode', ''); }}
                                    disabled={loadingDCA || isBusy}>
                                    <option value="">{loadingDCA ? 'Loading...' : 'Select DCA...'}</option>
                                    {dcaList.map(d => (
                                        <option key={d.DCAID} value={d.DCACode}>{d.DCAIDSTR}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={loadingDCA} />
                            </div>
                        </div>
                        <div className="col-span-6 sm:col-span-4">
                            {idx === 0 && <Label required>Sub-DCA</Label>}
                            <div className="relative">
                                <select className={`${inputCls} appearance-none pr-8`}
                                    value={row.sdcaCode}
                                    onChange={e => updateFn(row.id, 'sdcaCode', e.target.value)}
                                    disabled={!row.dcaCode || !!isLoadingSDCA || isBusy}>
                                    <option value="">
                                        {!row.dcaCode ? 'Select DCA first' : isLoadingSDCA ? 'Loading...' : 'Select Sub-DCA...'}
                                    </option>
                                    {rowSubDCAs.map(d => (
                                        <option key={d.SubDCACode} value={d.SubDCACode}>{d.SubDCAIDSTR}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={!!isLoadingSDCA} />
                            </div>
                        </div>
                        <div className="col-span-5 sm:col-span-2">
                            {idx === 0 && <Label required>Amount (₹)</Label>}
                            <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                value={row.amount}
                                onChange={e => updateFn(row.id, 'amount', e.target.value)}
                                disabled={isBusy} />
                        </div>
                        <div className="col-span-1 flex justify-center pb-1">
                            {rows.length > 1 && (
                                <button type="button" onClick={() => removeFn(row.id)} disabled={isBusy}
                                    className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors disabled:opacity-40">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    // ── Transport SubDCA list ─────────────────────────────────────────────────
    const tDCAs    = transportDCAs?.lstDCA    || [];
    const tSubDCAs = transportDCAs?.lstSubDCA || [];
    const filteredTSubDCAs = form.TransportDCA ? tSubDCAs : [];

    const hasBudgetWarning = typeof budgetResult === 'string' && budgetResult.trim();
    const thCls = 'px-3 py-2 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase';
    const tdCls = 'px-3 py-1.5';
    const sectionCls = 'pt-4 border-t border-gray-100 dark:border-gray-700';

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Receipt className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'Supplier PO Invoice Creation'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Purchase Module · Material Receipt — Supplier Invoice Entry</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: form ───────────────────────────────────────────── */}
                <div className="lg:col-span-2 space-y-4">

                    {/* MRR, invoice and GST */}
                    <Card>
                        <CardHeader icon={FileText} title="Invoice Details" />
                        <div className="p-4 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="sm:col-span-2">
                                    <Label required>MRR Number</Label>
                                    <div className="relative">
                                        <select
                                            className={`${inputCls} appearance-none pr-8`}
                                            value={form.MRRNo}
                                            onChange={e => handleMRRChange(e.target.value)}
                                            disabled={loadingMRRList || isBusy}
                                        >
                                            <option value="">{loadingMRRList ? 'Loading MRRs...' : 'Select MRR...'}</option>
                                            {mrrList.map(m => (
                                                <option key={m.MRRNo} value={m.MRRNo}>
                                                    {m.MRRNo}{m.VendorName ? ` — ${m.VendorName}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <SelectIcon loading={loadingMRRList || loadingVendorDetails} />
                                    </div>
                                </div>
                                <div>
                                    <Label required>Invoice Number</Label>
                                    <input className={inputCls} placeholder="Vendor's invoice number"
                                        value={form.InvoiceNo}
                                        onChange={e => setField('InvoiceNo', e.target.value)}
                                        disabled={isBusy} />
                                </div>
                                <div>
                                    <Label required>Invoice Date</Label>
                                    <CustomDatePicker value={form.InvoiceDate}
                                        onChange={val => setField('InvoiceDate', val)}
                                        format="DD-MMM-YYYY" placeholder="Invoice date" />
                                </div>
                                <div>
                                    <Label required>Invoice Making Date</Label>
                                    <CustomDatePicker value={form.InvoiceMakingDate}
                                        onChange={val => setField('InvoiceMakingDate', val)}
                                        format="DD-MMM-YYYY" placeholder="Making date" />
                                </div>
                            </div>

                            {/* Auto-filled PO/Vendor details */}
                            {form.CCCode && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <InfoBadge label="Vendor"      value={form.VendorName} />
                                    <InfoBadge label="PO Number"   value={form.PONo} />
                                    <InfoBadge label="CC Code"     value={form.CCCode} />
                                    <InfoBadge label="DCA Code"    value={form.DCACode} />
                                    <InfoBadge label="Vendor Type" value={form.VendorType} />
                                    <InfoBadge label="Advance"     value={`₹ ${fmt(form.Advance)}`} />
                                    <InfoBadge label="Asset PO"    value={form.isAssetPO ? 'Yes' : 'No'} />
                                </div>
                            )}

                            {/* GST */}
                            <div className={`${sectionCls} space-y-3`}>
                                <SubHeading icon={ShieldCheck} right={<YesNoToggle value={form.TaxApplicable} onChange={handleTaxApplicableChange} disabled={isBusy} />}>
                                    GST Applicable?<span className="text-rose-500">*</span>
                                </SubHeading>

                                {form.TaxApplicable === 'Yes' && (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <Label required>GST Type</Label>
                                                <div className="relative">
                                                    <select className={`${inputCls} appearance-none pr-8`}
                                                        value={form.GSTType}
                                                        onChange={e => handleGSTTypeChange(e.target.value)}
                                                        disabled={isBusy}>
                                                        <option value="">Select GST type...</option>
                                                        <option value="Creditable">Creditable</option>
                                                        <option value="Non-Creditable">Non-Creditable</option>
                                                    </select>
                                                    <SelectIcon loading={false} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <Label required>Vendor GST</Label>
                                                <div className="relative">
                                                    <select className={`${inputCls} appearance-none pr-8`}
                                                        value={form.VendorGST}
                                                        onChange={e => setField('VendorGST', e.target.value)}
                                                        disabled={!vendorTaxList.length || isBusy}>
                                                        <option value="">Select vendor GST...</option>
                                                        {vendorTaxList.map(g => (
                                                            <option key={g.TaxNoID || g.TaxNoName} value={g.TaxNoName}>{g.TaxNoName}</option>
                                                        ))}
                                                    </select>
                                                    <SelectIcon loading={false} />
                                                </div>
                                                {!vendorTaxList.length && form.VendorId && (
                                                    <p className="text-[11px] text-amber-500 mt-1">No GST numbers found for this vendor.</p>
                                                )}
                                            </div>
                                            <div>
                                                <Label required>Company GST</Label>
                                                <div className="relative">
                                                    <select className={`${inputCls} appearance-none pr-8`}
                                                        value={form.CompanyGST}
                                                        onChange={e => setField('CompanyGST', e.target.value)}
                                                        disabled={!form.GSTType || loadingCompanyGST || !companyGSTNos.length || isBusy}>
                                                        <option value="">Select company GST...</option>
                                                        {companyGSTNos.map(g => (
                                                            <option key={g.TaxNoID} value={g.GSTNo}>{g.TaxNoName}</option>
                                                        ))}
                                                    </select>
                                                    <SelectIcon loading={loadingCompanyGST} />
                                                </div>
                                            </div>
                                        </div>

                                        {loadingItemsData && (
                                            <div className="flex items-center gap-2 text-xs text-indigo-500">
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Fetching item details with GST...
                                            </div>
                                        )}

                                        {/* State badge — computed from first 2 digits of each GST number */}
                                        {form.VendorGST && form.CompanyGST && (
                                            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                                                sameState
                                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700'
                                                    : 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700'
                                            }`}>
                                                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                                                {sameState
                                                    ? `Same State (${form.VendorGST.substring(0,2)}) — CGST + SGST applicable`
                                                    : `Different State (Vendor: ${form.VendorGST.substring(0,2)} / Company: ${form.CompanyGST.substring(0,2)}) — IGST applicable`}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Card>

                    {/* MRR Items */}
                    {form.MRRNo && (
                        <Card>
                            <CardHeader icon={Package} title="MRR Items"
                                action={loadingItemsData
                                    ? <div className="flex items-center gap-1.5 text-xs text-indigo-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading...</div>
                                    : null}
                            />
                            <div className="p-4">
                                {!itemsData && !loadingItemsData && (
                                    <p className="text-xs text-center py-3 font-medium text-amber-600 dark:text-amber-400">
                                        {!form.TaxApplicable
                                            ? 'Select whether GST is applicable above to load items.'
                                            : form.TaxApplicable === 'Yes' && (!form.CompanyGST || !form.VendorGST)
                                                ? 'Select vendor and company GST numbers above — items will load with the correct GST amounts.'
                                                : <span className="text-gray-400">Loading items...</span>}
                                    </p>
                                )}

                                {itemsData && (
                                    <>
                                        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50 dark:bg-gray-900/40">
                                                    <tr>
                                                        <th className={`${thCls} text-left`}>#</th>
                                                        <th className={`${thCls} text-left`}>Item</th>
                                                        <th className={`${thCls} text-right`}>Qty</th>
                                                        <th className={`${thCls} text-right`}>Price</th>
                                                        <th className={`${thCls} text-right`}>Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                                    {(itemsData.MRRItemData || []).map((item, idx) => (
                                                        <tr key={item.Id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                                            <td className={`${tdCls} text-gray-400`}>{idx + 1}</td>
                                                            <td className={`${tdCls} max-w-[280px]`}>
                                                                <p className="text-gray-800 dark:text-gray-100 font-semibold truncate" title={item.itemname}>
                                                                    <span className="font-mono text-[11px] font-bold text-indigo-600 dark:text-indigo-400 mr-1.5">{item.itemcode || '—'}</span>
                                                                    {item.itemname || `Item ${idx + 1}`}
                                                                </p>
                                                                {item.specification && (
                                                                    <p className="text-[11px] text-gray-400 truncate" title={item.specification}>{item.specification}</p>
                                                                )}
                                                            </td>
                                                            <td className={`${tdCls} text-right font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap`}>
                                                                {item.Requestedqty ?? '—'} <span className="text-[11px] text-gray-400">{item.units || ''}</span>
                                                            </td>
                                                            <td className={`${tdCls} text-right font-mono text-gray-600 dark:text-gray-300 whitespace-nowrap`}>
                                                                ₹ {fmt(item.NewBasicprice || 0)}
                                                            </td>
                                                            <td className={`${tdCls} text-right font-mono font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap`}>
                                                                ₹ {fmt(item.Amount || 0)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="bg-indigo-50/60 dark:bg-indigo-900/10">
                                                    <tr className="font-bold text-gray-800 dark:text-gray-100">
                                                        <td colSpan={4} className={tdCls}>Items Total</td>
                                                        <td className={`${tdCls} text-right font-mono whitespace-nowrap`}>₹ {fmt(itemTotal)}</td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>

                                        {/* GST on items — one line */}
                                        {form.TaxApplicable === 'Yes' && gstTotal > 0 && (
                                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/15 border border-blue-100 dark:border-blue-800/30 text-xs">
                                                <span className="font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide">GST on Items</span>
                                                {sameState ? (
                                                    <>
                                                        <span className="text-gray-500 dark:text-gray-400">CGST <b className="text-blue-700 dark:text-blue-300">₹ {fmt(cgstTotal)}</b></span>
                                                        <span className="text-gray-500 dark:text-gray-400">SGST <b className="text-blue-700 dark:text-blue-300">₹ {fmt(sgstTotal)}</b></span>
                                                    </>
                                                ) : (
                                                    <span className="text-gray-500 dark:text-gray-400">IGST <b className="text-orange-600 dark:text-orange-400">₹ {fmt(igstTotal)}</b></span>
                                                )}
                                                <span className="ml-auto font-semibold text-gray-600 dark:text-gray-300">Total <b className="text-indigo-700 dark:text-indigo-300">₹ {fmt(gstTotal)}</b></span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </Card>
                    )}

                    {/* Transport, other charges & deductions */}
                    <Card>
                        <CardHeader icon={Layers} title="Charges & Deductions" />
                        <div className="p-4 space-y-4">

                            {/* Transport */}
                            <div>
                                <SubHeading icon={Truck}>
                                    Transport Charges <span className="font-normal normal-case text-gray-400">(optional)</span>
                                </SubHeading>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <div>
                                        <Label>Transport DCA</Label>
                                        <div className="relative">
                                            <select className={`${inputCls} appearance-none pr-8`}
                                                value={form.TransportDCA}
                                                onChange={e => { setField('TransportDCA', e.target.value); setField('TransportSubDCA', ''); }}
                                                disabled={loadingTransportDCAs || isBusy}>
                                                <option value="">{loadingTransportDCAs ? 'Loading...' : 'Select DCA...'}</option>
                                                {tDCAs.map(d => (
                                                    <option key={d.DCAID || d.DCACode} value={d.DCACode}>{d.DCAIDSTR || d.DCACode}</option>
                                                ))}
                                            </select>
                                            <SelectIcon loading={loadingTransportDCAs} />
                                        </div>
                                    </div>
                                    <div>
                                        <Label>Transport Sub-DCA</Label>
                                        <div className="relative">
                                            <select className={`${inputCls} appearance-none pr-8`}
                                                value={form.TransportSubDCA}
                                                onChange={e => setField('TransportSubDCA', e.target.value)}
                                                disabled={!form.TransportDCA || isBusy}>
                                                <option value="">{!form.TransportDCA ? 'Select DCA first' : 'Select Sub-DCA...'}</option>
                                                {filteredTSubDCAs.map(d => (
                                                    <option key={d.SubDCACode} value={d.SubDCACode}>{d.SubDCAIDSTR || d.SubDCACode}</option>
                                                ))}
                                            </select>
                                            <SelectIcon loading={false} />
                                        </div>
                                    </div>
                                    <div>
                                        <Label>Amount (₹)</Label>
                                        <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                            value={form.TransportAmount}
                                            onChange={e => setField('TransportAmount', e.target.value)}
                                            disabled={isBusy} />
                                    </div>
                                    <div>
                                        <Label>GST %</Label>
                                        <input className={inputCls} type="number" min="0" max="100" step="0.01" placeholder="0"
                                            value={form.TransportGSTPct}
                                            onChange={e => setField('TransportGSTPct', e.target.value)}
                                            disabled={isBusy} />
                                    </div>
                                </div>
                                {transportAmt > 0 && transportGSTPct > 0 && (
                                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/15 border border-amber-100 dark:border-amber-800/30 text-xs">
                                        <span className="text-gray-500 dark:text-gray-400">GST @ {transportGSTPct}% <b className="text-amber-700 dark:text-amber-300">₹ {fmt(transportGSTAmt)}</b></span>
                                        {sameState ? (
                                            <>
                                                <span className="text-gray-500 dark:text-gray-400">CGST <b className="text-blue-700 dark:text-blue-300">₹ {fmt(transportCGSTCalc)}</b></span>
                                                <span className="text-gray-500 dark:text-gray-400">SGST <b className="text-blue-700 dark:text-blue-300">₹ {fmt(transportSGSTCalc)}</b></span>
                                            </>
                                        ) : (
                                            <span className="text-gray-500 dark:text-gray-400">IGST <b className="text-orange-600 dark:text-orange-400">₹ {fmt(transportIGSTCalc)}</b></span>
                                        )}
                                        <span className="ml-auto font-semibold text-gray-600 dark:text-gray-300">Total incl. GST <b className="text-gray-900 dark:text-white">₹ {fmt(transportAmt + transportGSTAmt)}</b></span>
                                    </div>
                                )}
                            </div>

                            {/* Other charges */}
                            <div className={sectionCls}>
                                <SubHeading icon={Plus} right={
                                    <div className="flex items-center gap-3">
                                        {otherTotal > 0 && <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">₹ {fmt(otherTotal)}</span>}
                                        <button type="button" onClick={addOtherRow} disabled={isBusy}
                                            className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 font-semibold disabled:opacity-40">
                                            <Plus className="h-3.5 w-3.5" /> Add Row
                                        </button>
                                    </div>
                                }>
                                    Other Charges <span className="font-normal normal-case text-gray-400">(optional)</span>
                                </SubHeading>
                                {renderChargeRows(otherCharges, otherDCAs, loadingOtherDCAs, updateOtherRow, removeOtherRow)}
                            </div>

                            {/* Deductions */}
                            <div className={sectionCls}>
                                <SubHeading icon={Layers} right={
                                    <div className="flex items-center gap-3">
                                        {deductTotal > 0 && <span className="text-xs font-bold text-rose-700 dark:text-rose-300">₹ {fmt(deductTotal)}</span>}
                                        <button type="button" onClick={addDeductRow} disabled={isBusy}
                                            className="flex items-center gap-1 text-xs text-rose-600 dark:text-rose-400 hover:text-rose-700 font-semibold disabled:opacity-40">
                                            <Plus className="h-3.5 w-3.5" /> Add Row
                                        </button>
                                    </div>
                                }>
                                    Deductions <span className="font-normal normal-case text-gray-400">(optional)</span>
                                </SubHeading>
                                {renderChargeRows(deductions, deductionDCAs, loadingDeductDCAs, updateDeductRow, removeDeductRow)}
                            </div>
                        </div>
                    </Card>

                    {/* TCS, exchange, e-way bill, retention/hold, attachment */}
                    <Card>
                        <CardHeader icon={Paperclip} title="Additional Details & Attachment" />
                        <div className="p-4 space-y-4">

                            {/* TCS + Exchange side by side */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <SubHeading icon={Calculator} right={<YesNoToggle value={form.TCSApplicable} onChange={val => setField('TCSApplicable', val)} disabled={isBusy} />}>
                                        TCS Applicable?<span className="text-rose-500">*</span>
                                    </SubHeading>
                                    {form.TCSApplicable === 'Yes' && (
                                        <div>
                                            <Label required>TCS Amount (₹)</Label>
                                            <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                                value={form.TCSAmount}
                                                onChange={e => setField('TCSAmount', e.target.value)}
                                                disabled={isBusy} />
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <SubHeading icon={ArrowLeftRight} right={
                                        <YesNoToggle value={form.isExchangeExist}
                                            onChange={val => { setField('isExchangeExist', val); setField('ExchangeItem', ''); setField('ExItemAmount', ''); }}
                                            disabled={isBusy} />
                                    }>
                                        Exchange Item?<span className="text-rose-500">*</span>
                                    </SubHeading>
                                    {form.isExchangeExist === 'Yes' && (
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <Label required>Description</Label>
                                                <input className={inputCls} placeholder="Exchanged item"
                                                    value={form.ExchangeItem}
                                                    onChange={e => setField('ExchangeItem', e.target.value)}
                                                    disabled={isBusy} />
                                            </div>
                                            <div>
                                                <Label required>Amount (₹)</Label>
                                                <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                                    value={form.ExItemAmount}
                                                    onChange={e => setField('ExItemAmount', e.target.value)}
                                                    disabled={isBusy} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* E-Way Bill */}
                            <div className={`${sectionCls} space-y-2`}>
                                <SubHeading icon={Truck} right={<YesNoToggle value={form.EWaybillApplicable} onChange={val => setField('EWaybillApplicable', val)} disabled={isBusy} />}>
                                    E-Way Bill Applicable?<span className="text-rose-500">*</span>
                                </SubHeading>
                                {form.EWaybillApplicable === 'Yes' && (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                                        <div>
                                            <Label required>E-Way Bill No.</Label>
                                            <input className={inputCls} placeholder="E-Way bill no."
                                                value={form.EWaybillNumber}
                                                onChange={e => setField('EWaybillNumber', e.target.value)}
                                                disabled={isBusy} />
                                        </div>
                                        <div>
                                            <Label required>Vehicle No.</Label>
                                            <input className={inputCls} placeholder="Vehicle no."
                                                value={form.VehicleNumber}
                                                onChange={e => setField('VehicleNumber', e.target.value)}
                                                disabled={isBusy} />
                                        </div>
                                        <div>
                                            <Label>Transporter</Label>
                                            <input className={inputCls} placeholder="Transporter name"
                                                value={form.TransporterName}
                                                onChange={e => setField('TransporterName', e.target.value)}
                                                disabled={isBusy} />
                                        </div>
                                        <div>
                                            <Label>LR Number</Label>
                                            <input className={inputCls} placeholder="LR no."
                                                value={form.LRNumber}
                                                onChange={e => setField('LRNumber', e.target.value)}
                                                disabled={isBusy} />
                                        </div>
                                        <div>
                                            <Label>E-Way Bill Date</Label>
                                            <CustomDatePicker value={form.EWaybillDate}
                                                onChange={val => setField('EWaybillDate', val)}
                                                format="DD-MMM-YYYY" placeholder="Date" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Retention / Hold / Advance */}
                            <div className={`${sectionCls} grid grid-cols-1 sm:grid-cols-3 gap-3`}>
                                <div>
                                    <Label>Retention (₹)</Label>
                                    <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                        value={form.Retention} onChange={e => setField('Retention', e.target.value)} disabled={isBusy} />
                                </div>
                                <div>
                                    <Label>Hold (₹)</Label>
                                    <input className={inputCls} type="number" min="0" step="0.01" placeholder="0.00"
                                        value={form.Hold} onChange={e => setField('Hold', e.target.value)} disabled={isBusy} />
                                </div>
                                <div>
                                    <Label>Advance (₹)</Label>
                                    <input className={readonlyCls} value={fmt(form.Advance)} readOnly title="Auto-filled from PO" />
                                </div>
                            </div>

                            {/* Invoice PDF */}
                            <div className={sectionCls}>
                                <Label>Invoice PDF</Label>

                                {uploadStatus === 'idle' && (
                                    <label className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg border-2 border-dashed border-indigo-300 dark:border-indigo-700 bg-indigo-50/40 dark:bg-indigo-900/10 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:border-indigo-400 cursor-pointer transition-all">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="application/pdf"
                                            className="hidden"
                                            onChange={e => handleFileSelect(e.target.files?.[0])}
                                        />
                                        <CloudUpload className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                                        <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">Click to attach invoice PDF</span>
                                        <span className="text-xs text-gray-400 ml-auto">PDF only · Max 10 MB</span>
                                    </label>
                                )}

                                {uploadStatus === 'ready' && attachedFile && (
                                    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/10">
                                        <Paperclip className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{attachedFile.name}</p>
                                            <p className="text-[11px] text-indigo-500 dark:text-indigo-400">
                                                {(attachedFile.size / 1024).toFixed(0)} KB · Uploads to S3 after the invoice is saved
                                            </p>
                                        </div>
                                        <button type="button" onClick={handleRemoveFile} disabled={isBusy}
                                            className="p-1 rounded-md text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors flex-shrink-0"
                                            title="Remove attachment">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}

                                {uploadStatus === 'uploading' && attachedFile && (
                                    <div className="px-3 py-2 rounded-lg border border-indigo-200 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/10 space-y-2">
                                        <div className="flex items-center gap-3">
                                            <Upload className="h-4 w-4 text-indigo-500 animate-bounce flex-shrink-0" />
                                            <p className="flex-1 min-w-0 text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{attachedFile.name}</p>
                                            <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400 flex-shrink-0">{uploadProgress}%</span>
                                        </div>
                                        <div className="w-full bg-indigo-100 dark:bg-indigo-900/40 rounded-full h-2 overflow-hidden">
                                            <div
                                                className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300 ease-out"
                                                style={{ width: `${uploadProgress}%` }}
                                            />
                                        </div>
                                        <p className="text-[11px] text-indigo-500 dark:text-indigo-400 font-medium">
                                            Uploading PDF to S3... please do not close or refresh the page.
                                        </p>
                                    </div>
                                )}

                                {uploadStatus === 'error' && (
                                    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-rose-200 dark:border-rose-700 bg-rose-50/60 dark:bg-rose-900/10">
                                        <AlertCircle className="h-4 w-4 text-rose-500 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">Upload Failed</p>
                                            <p className="text-[11px] text-rose-500 dark:text-rose-400 truncate">{uploadError}</p>
                                        </div>
                                        <button type="button" onClick={handleRemoveFile}
                                            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/30 hover:bg-rose-200 transition-colors flex-shrink-0">
                                            <RotateCcw className="h-3.5 w-3.5" /> Retry
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <Card>
                        <CardHeader icon={Calculator} title="Invoice Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                <SumRow label="MRR No."      value={form.MRRNo} />
                                <SumRow label="Vendor"       value={form.VendorName} />
                                <SumRow label="PO / CC"      value={[form.PONo, form.CCCode].filter(Boolean).join(' / ')} />
                                <SumRow label="Invoice No."  value={form.InvoiceNo} />
                                <SumRow label="Invoice Date" value={formatDateForAPI(form.InvoiceDate)} />
                            </dl>

                            <dl className="space-y-1.5 text-xs pt-3 border-t border-gray-100 dark:border-gray-700">
                                <SumRow label="Basic Value (Items)" value={`₹ ${fmt(itemTotal)}`} />
                                {form.TaxApplicable === 'Yes' && gstTotal > 0 && (
                                    sameState ? (
                                        <>
                                            <SumRow label="Items CGST" value={`₹ ${fmt(cgstTotal)}`} />
                                            <SumRow label="Items SGST" value={`₹ ${fmt(sgstTotal)}`} />
                                        </>
                                    ) : (
                                        <SumRow label="Items IGST" value={`₹ ${fmt(igstTotal)}`} />
                                    )
                                )}
                                {transportAmt > 0 && <SumRow label="Transport" value={`₹ ${fmt(transportAmt)}`} />}
                                {transportGSTAmt > 0 && <SumRow label={`Transport GST (${sameState ? 'CGST+SGST' : 'IGST'})`} value={`₹ ${fmt(transportGSTAmt)}`} />}
                                {(gstTotal > 0 || transportGSTAmt > 0) && (
                                    <SumRow label="Total GST" value={`₹ ${fmt(gstTotal + transportGSTAmt)}`} />
                                )}
                                {otherTotal > 0 && <SumRow label="Other Charges" value={`₹ ${fmt(otherTotal)}`} />}
                                <div className="flex justify-between gap-3 pt-1.5 border-t border-gray-100 dark:border-gray-700 font-bold text-gray-800 dark:text-gray-100">
                                    <dt>Invoice Total</dt>
                                    <dd>₹ {fmt(invoiceValue)}</dd>
                                </div>
                                {[
                                    { label: 'Deductions',    val: deductTotal },
                                    { label: 'Retention',     val: parseFloat(form.Retention) || 0 },
                                    { label: 'Hold',          val: parseFloat(form.Hold) || 0 },
                                    { label: 'Advance',       val: parseFloat(form.Advance) || 0 },
                                    { label: 'Exchange Item', val: exchangeAmt },
                                ].filter(x => x.val > 0).map(({ label, val }) => (
                                    <div key={label} className="flex justify-between gap-3 text-rose-600 dark:text-rose-400">
                                        <dt>— {label}</dt>
                                        <dd className="font-semibold">₹ {fmt(val)}</dd>
                                    </div>
                                ))}
                                {tcsAmount > 0 && (
                                    <div className="flex justify-between gap-3 text-emerald-600 dark:text-emerald-400">
                                        <dt>+ TCS</dt>
                                        <dd className="font-semibold">₹ {fmt(tcsAmount)}</dd>
                                    </div>
                                )}
                            </dl>

                            <div className={`px-4 py-3 rounded-lg text-white bg-gradient-to-r ${netAmount < 0 ? 'from-rose-500 to-rose-600' : 'from-indigo-500 to-violet-600'}`}>
                                <p className="text-white/80 text-[10px] font-semibold uppercase tracking-wider">Net Amount</p>
                                <p className="text-2xl font-black">₹ {fmt(netAmount)}</p>
                            </div>

                            {/* Budget result */}
                            {budgetResult !== null && (
                                <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${
                                    hasBudgetWarning
                                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200'
                                        : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-800 dark:text-green-200'
                                }`}>
                                    {hasBudgetWarning
                                        ? <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                        : <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />}
                                    <div className="min-w-0">
                                        <p className="font-semibold">{hasBudgetWarning ? 'Budget Warning' : 'Budget Check Passed'}</p>
                                        <p className="break-words">{hasBudgetWarning ? budgetResult : 'Sufficient budget is available for this invoice.'}</p>
                                    </div>
                                </div>
                            )}

                            <button type="button" onClick={handleCheckBudget} disabled={isBusy || !form.CCCode}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm disabled:opacity-60 ${
                                    budgetChecked
                                        ? 'bg-green-500 hover:bg-green-600 text-white'
                                        : 'bg-amber-500 hover:bg-amber-600 text-white'
                                }`}>
                                {loadingBudget
                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                    : budgetChecked ? <CheckCircle2 className="h-4 w-4" /> : <Calculator className="h-4 w-4" />}
                                {budgetChecked ? 'Budget Checked' : 'Check Budget'}
                            </button>

                            <div className="flex gap-2">
                                <button type="button" onClick={handleReset} disabled={isBusy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button type="button" onClick={handleSubmit} disabled={isBusy}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60 text-white text-sm font-bold transition-all shadow-md">
                                    {isBusy
                                        ? <Loader2 className="h-4 w-4 animate-spin" />
                                        : <Send className="h-4 w-4" />}
                                    {uploadStatus === 'uploading'
                                        ? `Uploading PDF ${uploadProgress}%...`
                                        : loadingBudget ? 'Checking Budget...'
                                        : loadingSave   ? 'Saving Invoice...'
                                        : 'Submit Invoice'}
                                </button>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default SupplierPOInvoiceCreation;
